import { ApplicationContext } from '@wix/editor-platform-contexts';
import { BaseSDKShape } from '../BaseSDKShape';
import { PlatformSDKShape } from '../PlatformSDKShape';
import {
  BindingCandidate,
  BindingDetailsMap,
  BindingProperties,
  BindingTargetDetails,
  findBindingCandidatesOptions,
  ListBindingsOptions,
  SelectBindingOptions,
  UnbindAll,
} from '@wix/public-editor-platform-interfaces';

export class BindingsPublicSDKShape extends BaseSDKShape {
  constructor(
    protected overriddenApplicationContext: Promise<ApplicationContext> | null = null,
    private childPath: number[] = [],
  ) {
    super(overriddenApplicationContext);
  }

  // uses the bindings map to bind component props to provider items
  /**
   * @deprecated Use {@link setBinding} instead, which takes a flat
   * property-name -> binding dictionary with provider details inlined.
   */
  async bind(bindings: BindingProperties[]): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    const result = await privateAPI.bindings.bind(
      manifestContextId,
      this.childPath,
      bindings,
    );
    return result;
  }

  async setBinding(bindings: BindingDetailsMap): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    const result = await privateAPI.bindings.setBinding(
      manifestContextId,
      this.childPath,
      bindings,
    );
    return result;
  }

  // removes the bindings of specific props from the component and provider bindings map
  // if props is 'ALL' - unbinds all props from the component
  async unbind(props: string[] | UnbindAll): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    const result = await privateAPI.bindings.unbind(
      manifestContextId,
      this.childPath,
      props,
    );
    return result;
  }

  // gets the binding map for all attached providers of the editor pointer
  //   providerTypes: optionally filter by provider types
  //   includeDisplayNames: optionally add props and context items display names
  /**
   * @deprecated Use {@link getBinding} instead, which returns a flat
   * property-name -> binding dictionary with provider details inlined.
   */
  async listBindings(
    options?: ListBindingsOptions,
  ): Promise<BindingProperties[]> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    const result = await privateAPI.bindings.listBindings(
      manifestContextId,
      this.childPath,
      options,
    );

    return result;
  }

  // gets the flat property-name -> binding map for all attached providers of the editor pointer
  //   providerTypes: optionally filter by provider types
  //   includeDisplayNames: optionally add context items display names
  async getBinding(options?: ListBindingsOptions): Promise<BindingDetailsMap> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    const result = await privateAPI.bindings.getBinding(
      manifestContextId,
      this.childPath,
      options,
    );

    return result;
  }

  async findBindingCandidates(
    bindingTargetDetails: BindingTargetDetails,
    options?: findBindingCandidatesOptions,
  ): Promise<BindingCandidate[]> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    return privateAPI.bindings.findBindingCandidates(
      manifestContextId,
      this.childPath,
      bindingTargetDetails,
      options,
    );
  }

  async selectBinding(options: SelectBindingOptions): Promise<void> {
    const privateAPI = await this.getPlatformPrivateAPI();
    const manifestContextId = await this.getRequiredManifestContextId();
    await privateAPI.bindings.selectBinding(
      manifestContextId,
      this.childPath,
      options,
    );
  }
}

export const bindingsPublicSDKShape = new PlatformSDKShape(
  BindingsPublicSDKShape,
)
  .withPublicMethod('bind')
  .withPublicMethod('setBinding')
  .withPublicMethod('unbind')
  .withPublicMethod('listBindings')
  .withPublicMethod('getBinding')
  .withPublicMethod('findBindingCandidates')
  .withPublicMethod('selectBinding')
  .build();
